import {
    type CSSProperties,
    type ReactNode,
    useEffect,
    useState
} from "react";
import {
    View
} from "react-native";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitTheme,
    Loading,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    type DocsStyleTokens,
    BulletList,
    Paragraph,
    CodeBlock,
    DemoFrame,
    Article,
    Preview,
    Section
} from "../../components/content";

const NAME = "Loading";

const BRAND_CODE = `<Loading
    variant="brand"
    size="large"
/>`;

const USAGE_CODE = `<View
    role="status"
>
    <Loading
        size="small"
    />
    <Text>
        Refreshing…
    </Text>
</View>

<Loading
    variant="dots"
    pattern="ripple"
    size="large"
/>

<Button
    renderLoading={() => <Loading
        variant="dots"
        surface="filled"
        size="small"
        aria-hidden={true}
    />}
    onPress={handleSave}
    isLoading={isSaving}
    title="Save"
/>`;

const SIZES = [
    "small",
    "medium",
    "large"
] as const;

const COLORS = [
    "default",
    "mid",
    "emphasized",
    "success",
    "danger"
] as const;

const PATTERNS = [
    "lattice",
    "ripple",
    "columns",
    "scatter",
    "dither",
    "checker",
    "halftone",
    "noise",
    "rain",
    "honeycomb"
] as const;

const BUSY_DELAY = 1600;

const getRowStyle = ({
    spaces
}: DocsStyleTokens): CSSProperties => ({
    justifyContent: "center",
    gap: spaces.spacingMd,
    alignItems: "center",
    flexWrap: "wrap",
    display: "flex"
});

const getStatusStyle = ({
    shapeConfigs,
    spaces
}: DocsStyleTokens): CSSProperties => ({
    width: shapeConfigs.fieldMinWidth,
    gap: spaces.spacingSm,
    alignItems: "center",
    display: "flex"
});

const getItemStyle = ({
    spaces
}: DocsStyleTokens): CSSProperties => ({
    flexDirection: "column",
    gap: spaces.spacingSm,
    alignItems: "center",
    display: "flex"
});

const getItemsStyle = ({
    spaces
}: DocsStyleTokens): CSSProperties => ({
    justifyContent: "center",
    alignItems: "flex-end",
    gap: spaces.spacingLg,
    flexWrap: "wrap",
    display: "flex"
});

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("section.sizes"),
        id: "sizes"
    },
    {
        title: t("loadingPage.colorTitle"),
        id: "color"
    },
    {
        title: t("loadingPage.dotsTitle"),
        id: "dots"
    },
    {
        title: t("loadingPage.brandTitle"),
        id: "brand"
    },
    {
        title: t("loadingPage.buttonTitle"),
        id: "button"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const LoadingPage = () => {
    const docsTokens = NCoreUIKitTheme.useContext();

    const {
        t
    } = useDocsI18n();

    const [
        isRefreshing,
        setIsRefreshing
    ] = useState(false);

    const [
        isSaving,
        setIsSaving
    ] = useState(false);

    useEffect(() => {
        if(!isRefreshing) {
            return;
        }

        const timer = window.setTimeout(() => setIsRefreshing(false), BUSY_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        isRefreshing
    ]);

    useEffect(() => {
        if(!isSaving) {
            return;
        }

        const timer = window.setTimeout(() => setIsSaving(false), BUSY_DELAY);

        return () => {
            window.clearTimeout(timer);
        };
    }, [
        isSaving
    ]);

    const handleRefresh = () => setIsRefreshing(true);

    const handleSave = () => setIsSaving(true);

    const renderFilledDots = () => <Loading
        aria-hidden={true}
        surface="filled"
        variant="dots"
        size="small"
    />;

    const renderTonalDots = () => <Loading
        aria-hidden={true}
        surface="tonal"
        variant="dots"
        size="small"
        type="info"
    />;

    const renderItem = (label: string, node: ReactNode) => <div
        style={getItemStyle(docsTokens)}
        key={label}
    >
        {node}
        <Text
            variant="bodySmallSize"
            color="low"
        >
            {label}
        </Text>
    </div>;

    return <Article>
        <ComponentHeader
            lead={t("loadingPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("loadingPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("loadingPage.overviewCaption")}
            >
                <div
                    style={getRowStyle(docsTokens)}
                >
                    <Button
                        title={t("loadingPage.refresh")}
                        isDisabled={isRefreshing}
                        onPress={handleRefresh}
                        spreadBehaviour="free"
                        variant="outline"
                        type="neutral"
                        size="small"
                    />
                    <div
                        style={getStatusStyle(docsTokens)}
                        role="status"
                    >
                        {
                            isRefreshing ?
                                <Loading
                                    size="small"
                                />
                            :
                                null
                        }
                        <Text
                            variant="bodyMediumSize"
                            color="mid"
                        >
                            {isRefreshing ? t("loadingPage.refreshing") : t("loadingPage.upToDate")}
                        </Text>
                    </div>
                </div>
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("section.sizes")}
            id="sizes"
        >
            <Paragraph>
                {t("loadingPage.sizesText")}
            </Paragraph>
            <Preview>
                <div
                    style={getItemsStyle(docsTokens)}
                >
                    {
                        SIZES.map((size) => renderItem(`size="${size}"`, <Loading
                            size={size}
                        />))
                    }
                    {
                        renderItem("size={avatarSizes.xSmall}", <Loading
                            size={docsTokens.avatarSizes.xSmall}
                        />)
                    }
                </div>
            </Preview>
        </Section>
        <Section
            title={t("loadingPage.colorTitle")}
            id="color"
        >
            <Paragraph>
                {t("loadingPage.colorText")}
            </Paragraph>
            <Preview>
                <div
                    style={getItemsStyle(docsTokens)}
                >
                    {
                        COLORS.map((color) => renderItem(`color="${color}"`, <Loading
                            color={color}
                            size="large"
                        />))
                    }
                    {
                        renderItem("customColor={colors.content.text.info}", <Loading
                            customColor={docsTokens.colors.content.text.info}
                            size="large"
                        />)
                    }
                </div>
            </Preview>
        </Section>
        <Section
            title={t("loadingPage.dotsTitle")}
            id="dots"
        >
            <Paragraph>
                {t("loadingPage.dotsText")}
            </Paragraph>
            <Preview>
                <div
                    style={getItemsStyle(docsTokens)}
                >
                    {
                        PATTERNS.map((pattern) => renderItem(pattern, <div
                            style={{
                                width: docsTokens.avatarSizes.large
                            }}
                        >
                            <DemoFrame
                                height={docsTokens.avatarSizes.large}
                            >
                                <View
                                    style={{
                                        justifyContent: "center",
                                        alignItems: "center",
                                        flex: 1
                                    }}
                                >
                                    <Loading
                                        pattern={pattern}
                                        variant="dots"
                                        size="large"
                                    />
                                </View>
                            </DemoFrame>
                        </div>))
                    }
                </div>
            </Preview>
        </Section>
        <Section
            title={t("loadingPage.brandTitle")}
            id="brand"
        >
            <Paragraph>
                {t("loadingPage.brandText")}
            </Paragraph>
            <Preview>
                <div
                    style={getItemsStyle(docsTokens)}
                >
                    {
                        renderItem("variant=\"brand\"", <Loading
                            variant="brand"
                            size="large"
                        />)
                    }
                    {
                        renderItem("color=\"emphasized\"", <Loading
                            color="emphasized"
                            variant="brand"
                            size="large"
                        />)
                    }
                    {
                        renderItem("size={32}", <Loading
                            variant="brand"
                            size={32}
                        />)
                    }
                </div>
            </Preview>
            <CodeBlock
                code={BRAND_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("loadingPage.buttonTitle")}
            id="button"
        >
            <Paragraph>
                {t("loadingPage.buttonText")}
            </Paragraph>
            <Preview
                caption={t("loadingPage.buttonCaption")}
            >
                <div
                    style={getItemsStyle(docsTokens)}
                >
                    {
                        renderItem("isLoading", <Button
                            title={t("sample.action")}
                            spreadBehaviour="free"
                            isLoading={isSaving}
                            onPress={handleSave}
                        />)
                    }
                    {
                        renderItem("surface=\"filled\"", <Button
                            renderLoading={renderFilledDots}
                            title={t("sample.action")}
                            spreadBehaviour="free"
                            isLoading={isSaving}
                            onPress={handleSave}
                        />)
                    }
                    {
                        renderItem("surface=\"tonal\" type=\"info\"", <Button
                            renderLoading={renderTonalDots}
                            title={t("sample.action")}
                            spreadBehaviour="free"
                            isLoading={isSaving}
                            onPress={handleSave}
                            variant="tonal"
                            type="info"
                        />)
                    }
                </div>
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("loadingPage.a11yIcon"),
                    t("loadingPage.a11yDots"),
                    t("loadingPage.a11yMotion"),
                    t("loadingPage.a11yBusy")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("loadingPage.propVariant"),
                        type: "\"icon\" | \"dots\" | \"brand\"",
                        defaultValue: "\"icon\"",
                        name: "variant"
                    },
                    {
                        type: "\"small\" | \"medium\" | \"large\" | number",
                        description: t("loadingPage.propSize"),
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("loadingPage.propColor"),
                        type: "keyof IconContentColors",
                        defaultValue: "\"default\"",
                        name: "color"
                    },
                    {
                        description: t("loadingPage.propCustomColor"),
                        type: "keyof ProjectColorPalette | string",
                        name: "customColor"
                    },
                    {
                        type: "\"lattice\" | \"ripple\" | \"columns\" | \"scatter\" | \"dither\" | \"checker\" | \"halftone\" | \"noise\" | \"rain\" | \"honeycomb\"",
                        description: t("loadingPage.propPattern"),
                        defaultValue: "\"lattice\"",
                        name: "pattern"
                    },
                    {
                        type: "\"default\" | \"filled\" | \"tonal\"",
                        description: t("loadingPage.propSurface"),
                        defaultValue: "\"default\"",
                        name: "surface"
                    },
                    {
                        type: "\"primary\" | \"neutral\" | \"success\" | \"warning\" | \"danger\" | \"info\"",
                        description: t("loadingPage.propType"),
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("loadingPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("loadingPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("loadingPage.propsNote")}
            </Paragraph>
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default LoadingPage;
